import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Drawer from "@mui/material/Drawer";
import { classRequestActions } from "../../Store/ClassRequest";
import {
  getClassRequest,
  getRosterDetails,
  getTraineeDetailsForIDMTicketByRosterId,
  getRosterConfigByRosterId,
  getConfigurationValues,
} from "services/userServices";
import ClassRequest from "../../components/ClassRequestDrawer/ClassRequestDrawer";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import SnackBar from "components/SnackBar/SnackBar";
import applicationUsageLog from "../../utils/applicationUsagesLog";
import { askNowActions } from "Store/AskNowTicket";
import BackdropComponent from "components/Spinner/Spinner";
import CreateIdmTickets from "components/RosterConfig/CreateIdmTickets";
import { concatenateDateTime } from "components/RosterConfig/RosterConfigInputs";
import { Alert, Divider, Paper, Tooltip, Typography } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import { ReadOnlyTextFieldtheme } from "components/IdmCredentialTracking/CreateUpdateIdmRecord";
import { CustomButton } from "components/RosterConfig/RosterConfigStyle";

const idmWarning =
  "Production Training date,Production start date and (TCI or TTD) date requires to enable create button for IDM ticket creation.";
const idmWarningTTF =
  "The Production Start Date, Production Training Date/Time and TTF Details Date/Time must be provided to enable 'Create Ticket' button for IDM ticket creation.";

export default function CreateIDMTicket() {
  const [classReqDetails, setClassReqDetails] = React.useState([]);
  const assignTraineeData = useSelector((state) => state.assignTrainees);
  const [rosterList, setRosterList] = useState([]);
  const [classRequestIds, setClassRequestIds] = useState([]);
  const [selectedRoster, setSelectedRoster] = useState("");
  const [selectedClassRequest, setSelectedClassRequest] = useState("");
  const [loading, setLoading] = useState(false);
  const [showClassRequestDrawer, setShowClassRequestDrawer] = useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });
  const defaultRoster = assignTraineeData?.IDMRosters[0];
  const defaultClassRequestId = assignTraineeData?.classRequestId;
  const [rosterConfigDetails, setRosterConfigDetails] = useState({
    askNowTDT: "",
    techEquipmentSolutionId: null,
    ttfDateTime: null,
    tciDateTime: null,
    techTestDateTime: null,
    productionStartDate: null,
    productTrainingDateTime: null,
    trainerOracleId: null,
    trainerName: "",
    trainerEmail: "",
  });
  const getRosterId = () => {
    if (selectedRoster === "") {
      return defaultRoster;
    } else if (selectedRoster == 0) {
      return "";
    } else {
      return selectedRoster;
    }
  };

  const navigate = useNavigate();
  const [isTraineeDetailsFetched, setIsTraineeDetailsFetched] = useState(true);
  const [showCreateIdmTicket, setShowCreateIdmTicket] = useState(false);
  const [generateIdmTicketNew, setGenerateIdmTicketNew] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    const fetchConfigValue = async () => {
      try {
        const response = await getConfigurationValues(
          "appsettings:generateIdmTicketNew",
        );

        if (cancelled) return;

        if (response?.statusCode === 200) {
          if (!cancelled) {
            setGenerateIdmTicketNew(
              response?.data?.generateIdmTicketNew.toString() === "1",
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch config value:", error);
        }
      }
    };

    fetchConfigValue();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (defaultClassRequestId > 0) {
        try {
          if (!cancelled) {
            setRosterList(assignTraineeData.IDMRosters);
            setClassRequestIds(assignTraineeData.allClassRequestIds);
            setLoading(true);
          }

          const rosterId = getRosterId();
          dispatch(askNowActions.resetTraineeDetailsResponse());

          const traineeDetails =
            await getTraineeDetailsForIDMTicketByRosterId(rosterId);

          if (cancelled) return;

          if (traineeDetails.message === "Success") {
            if (!cancelled) {
              dispatch(
                askNowActions.updateTraineeDetailsResponse(traineeDetails),
              );
              setIsTraineeDetailsFetched(true);
            }
          }

          await getClassRequestDetails(defaultClassRequestId);

          if (cancelled) return;

          await fetchRosterConfigDetails(rosterId);

          if (!cancelled) {
            setLoading(false);
            applicationUsageLog(
              localStorage.getItem("User"),
              localStorage.getItem("EmailId"),
              "ASTT",
              "Create IDM",
            );
          }
        } catch (error) {
          if (!cancelled) {
            setLoading(false);
            console.error("Failed to initialize:", error);
          }
        }

        if (!cancelled && isTraineeDetailsFetched) {
          setIsTraineeDetailsFetched(false);
        }
      } else {
        navigate("/home");
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);
  // Helper function to safely get value with default
  const getValueOrDefault = (value, defaultValue) => {
    return value !== undefined ? value : defaultValue;
  };

  // Helper function to format date-time field
  const formatDateTime = (date, time) => {
    return date ? concatenateDateTime(date, time) : null;
  };

  // Helper function to format production date
  const formatProductionDate = (date) => {
    return date ? moment.utc(date).format("YYYY-MM-DD") : null;
  };

  // Helper function to build roster config object
  const buildRosterConfig = (data) => {
    const {
      AskNowTDT = "",
      TechEquipmentSolutionId,
      TTFDate,
      TTFTime,
      TCIDate,
      TCITime,
      TechTestDate,
      TechTestTime,
      ProductionDate,
      ProductTrainingDate,
      ProductTrainingTime,
      TrainerOracleId = null,
      TrainerName = "",
      TrainerEmail = "",
    } = data;

    return {
      askNowTDT: AskNowTDT,
      techEquipmentSolutionId: getValueOrDefault(TechEquipmentSolutionId, null),
      ttfDateTime: formatDateTime(TTFDate, TTFTime),
      tciDateTime: formatDateTime(TCIDate, TCITime),
      techTestDateTime: formatDateTime(TechTestDate, TechTestTime),
      productionStartDate: formatProductionDate(ProductionDate),
      productTrainingDateTime: formatDateTime(
        ProductTrainingDate,
        ProductTrainingTime,
      ),
      trainerOracleId: getValueOrDefault(TrainerOracleId, null),
      trainerName: getValueOrDefault(TrainerName, ""),
      trainerEmail: TrainerEmail || "",
    };
  };

  // Refactored main function
  const fetchRosterConfigDetails = async (rosterId) => {
    setLoading(true);

    try {
      const rosterConfigDetailsResponse = await getRosterConfigByRosterId(
        rosterId,
        false,
      );

      if (rosterConfigDetailsResponse.status === true) {
        const rosterConfig = buildRosterConfig(
          rosterConfigDetailsResponse.data[0],
        );
        setRosterConfigDetails(rosterConfig);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleRosterChange = (event, value, reason) => {
    setSelectedRoster(value);
    if (value) {
      fetchRosterConfigDetails(value);
    }
  };

  const handleClassRequestChange = (event, value, reason) => {
    if (value) {
      setSelectedClassRequest(value);
      getClassRequestDetails(value);
      fetchRosterList(value);
    }
  };

  const getClassRequestDetails = async (classReqId) => {
    setLoading(true);
    const response = await getClassRequest(classReqId);
    if (response.message === "Success") {
      setClassReqDetails(response?.data?.classRequestDetail);

      setLoading(false);
    } else {
      setLoading(false);
      setSnack({
        open: true,
        message: "failed to class request details",
        severity: "error",
      });
    }
  };

  // Helper function to filter active rosters
  const filterActiveRosters = (classRosters) => {
    return classRosters
      ?.filter((item) => item?.rosterStatusId !== 2)
      ?.map((item) => item.classRosterId);
  };

  // Helper function to handle successful roster fetch
  const handleSuccessfulRosterFetch = (
    rosters,
    setRosterList,
    setSelectedRoster,
    fetchRosterConfigDetails,
    setLoading,
    setSnack,
  ) => {
    if (rosters.length > 0) {
      setRosterList(rosters);
      setSelectedRoster(rosters[0]);
      fetchRosterConfigDetails(rosters[0]);
    } else {
      setLoading(false);
      setRosterList([]);
      setSelectedRoster(0);
      setSnack({
        open: true,
        message: "No active rosters found for the selected class request",
        severity: "warning",
      });
    }
  };

  // Helper function to handle failed roster fetch
  const handleFailedRosterFetch = (
    setLoading,
    setRosterList,
    setSelectedRoster,
    setSnack,
  ) => {
    setLoading(false);
    setRosterList([]);
    setSelectedRoster(0);
    setSnack({
      open: true,
      message: "failed to fetch roster details",
      severity: "error",
    });
  };

  // Refactored main function
  const fetchRosterList = async (classReqId) => {
    setLoading(true);

    const userDetails = await getRosterDetails(0, classReqId);

    if (userDetails.status === true) {
      const rosters = filterActiveRosters(userDetails?.data?.classRosters);
      handleSuccessfulRosterFetch(
        rosters,
        setRosterList,
        setSelectedRoster,
        fetchRosterConfigDetails,
        setLoading,
        setSnack,
      );
    } else {
      handleFailedRosterFetch(
        setLoading,
        setRosterList,
        setSelectedRoster,
        setSnack,
      );
    }
  };
  const isClassRequestSelected = () =>
    selectedClassRequest !== "" && defaultClassRequestId !== 1;

  const toggleClassRequestDrawer = (anchor, open) => (event) => {
    if (isClassRequestSelected) {
      if (
        event.type === "keydown" &&
        (event.key === "Tab" || event.key === "Shift")
      ) {
        return;
      }
      dispatch(classRequestActions.addClassRequest(classReqDetails));

      setShowClassRequestDrawer({ ...showClassRequestDrawer, [anchor]: open });
    } else {
      setSnack({
        open: true,
        message: "Please select a valid class request",
        severity: "warning",
      });
    }
  };

  const handleRosterConfig = () => {
    const rosterId = getRosterId();
    const classRequestId =
      selectedClassRequest === ""
        ? defaultClassRequestId
        : selectedClassRequest;
    const isRosterClone = false;

    navigate(
      `/new-roster-config/edit/${rosterId}/${classRequestId}/${isRosterClone}`,
      {
        state: "createIDMTicket",
      },
    );
  };

  const isEligibleForTicketCreation =
    rosterConfigDetails.productTrainingDateTime &&
    rosterConfigDetails.productionStartDate &&
    ((rosterConfigDetails.techEquipmentSolutionId !== 14 &&
      (rosterConfigDetails.tciDateTime ||
        rosterConfigDetails.techTestDateTime)) ||
      (rosterConfigDetails.techEquipmentSolutionId === 14 &&
        rosterConfigDetails.ttfDateTime));

  return (
    <>
      {loading && <BackdropComponent />}
      <Paper
        sx={{
          flexDirection: "column",
          boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.35)",
          padding: 3,
          overflow: "auto",
          height: "calc(100vh - 80px)",
          margin: 1.2,
        }}
      >
        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
        <Grid container>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontSize: 18,
                fontWeight: "bold",
                color: "#3F5765",
                letterSpacing: "0.10em",
              }}
            >
              Create IDM Ticket
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={2}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    fontWeight: "bold",
                    color: "#3F5765",
                    letterSpacing: "0.10em",
                    fontSize: 12,
                    marginTop: 0.75,
                    marginBottom: 0.75,
                  }}
                >
                  Class Request
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Autocomplete
                  onChange={handleClassRequestChange}
                  options={classRequestIds}
                  getOptionLabel={(item) => item.toString()}
                  value={
                    selectedClassRequest === ""
                      ? defaultClassRequestId
                      : selectedClassRequest
                  }
                  defaultValue={defaultClassRequestId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select ClassRequest"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          </Grid>

          <Grid item xs={2}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    fontWeight: "bold",
                    color: "#3F5765",
                    letterSpacing: "0.10em",
                    fontSize: 12,
                    marginTop: 0.75,
                    marginBottom: 0.75,
                  }}
                >
                  Class Roster
                </Typography>
              </Grid>
              <Grid item xs={11}>
                <Autocomplete
                  disablePortal
                  onChange={handleRosterChange}
                  options={rosterList}
                  getOptionLabel={(item) => item.toString()}
                  value={getRosterId()}
                  defaultValue={defaultRoster}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select ClassRoster"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    fontWeight: "bold",
                    color: "#3F5765",
                    letterSpacing: "0.10em",
                    fontSize: 12,
                    marginTop: 0.75,
                    marginBottom: 0.75,
                  }}
                >
                  Program
                </Typography>
              </Grid>

              <Grid item xs={11}>
                <ThemeProvider theme={ReadOnlyTextFieldtheme}>
                  <Tooltip title={classReqDetails?.program}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled
                      value={classReqDetails?.program}
                    ></TextField>
                  </Tooltip>
                </ThemeProvider>
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          </Grid>

          <Grid item xs={1.6} sx={{ paddingTop: 4 }}>
            <CustomButton
              variant="outlined"
              onClick={toggleClassRequestDrawer("right", true)}
              sx={{ color: "#3299FF" }}
            >
              Class Request Details
            </CustomButton>
          </Grid>

          <Grid item xs={1.4} sx={{ paddingTop: 4 }}>
            <CustomButton
              variant="outlined"
              sx={{ color: "#3299FF" }}
              onClick={handleRosterConfig}
              endIcon={<ArrowOutwardIcon />}
            >
              Roster config
            </CustomButton>
          </Grid>

          <Grid item xs={2}></Grid>

          <Grid
            item
            xs={12}
            sx={{ paddingTop: isEligibleForTicketCreation ? 4 : 3 }}
          >
            {isEligibleForTicketCreation ? (
              <CustomButton
                variant="contained"
                sx={{ backgroundColor: "#3299FF" }}
                onClick={() => {
                  if (generateIdmTicketNew) {
                    navigate("/home/generateidmticket", {
                      state: {
                        classRosterId: getRosterId(),
                        classRequestId:
                          selectedClassRequest === ""
                            ? defaultClassRequestId
                            : selectedClassRequest,
                        askNowTDT: rosterConfigDetails.askNowTDT,
                        ttfTrainerData: [
                          rosterConfigDetails.techEquipmentSolutionId === 14 &&
                          rosterConfigDetails.ttfDateTime
                            ? rosterConfigDetails.ttfDateTime
                            : null,
                          rosterConfigDetails.techEquipmentSolutionId !== 14 &&
                          rosterConfigDetails.tciDateTime
                            ? rosterConfigDetails.tciDateTime
                            : null,
                          rosterConfigDetails.techEquipmentSolutionId !== 14 &&
                          rosterConfigDetails.techTestDateTime
                            ? rosterConfigDetails.techTestDateTime
                            : null,
                          rosterConfigDetails.productionStartDate || null,
                          rosterConfigDetails.productTrainingDateTime || null,
                          rosterConfigDetails.trainerOracleId || null,
                          rosterConfigDetails.trainerName || "",
                          rosterConfigDetails.trainerEmail || "",
                          rosterConfigDetails.techEquipmentSolutionId === 14,
                        ],
                      },
                    });
                  } else setShowCreateIdmTicket(true);
                }}
              >
                Create IDM Ticket
              </CustomButton>
            ) : (
              <Alert
                severity="error"
                sx={{
                  fontFamily: "Urbanist, sans-serif",
                }}
              >
                {rosterConfigDetails.techEquipmentSolutionId !== 14
                  ? idmWarning
                  : idmWarningTTF}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12} sx={{ paddingTop: 2 }}>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    fontWeight: "bold",
                    color: "#3F5765",
                    letterSpacing: "0.10em",
                    fontSize: 12,
                    marginTop: 0.75,
                    marginBottom: 0.75,
                  }}
                >
                  Last Modified by
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                    fontSize: 12,
                    marginTop: 0.75,
                    marginBottom: 0.75,
                  }}
                >
                  {classReqDetails?.modifiedBy}
                  {classReqDetails?.modifiedDate ? " | " : ""}
                  {classReqDetails?.modifiedDate
                    ? moment
                        .utc(classReqDetails?.modifiedDate)
                        .format("YYYY-MM-DD hh:mm:ss A")
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={1}></Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {showCreateIdmTicket && (
              <CreateIdmTickets
                rosterId={getRosterId()}
                classRequestId={
                  selectedClassRequest === ""
                    ? defaultClassRequestId
                    : selectedClassRequest
                }
                askNowTDT={rosterConfigDetails.askNowTDT}
                ttfTrainerData={[
                  rosterConfigDetails.techEquipmentSolutionId === 14 &&
                  rosterConfigDetails.ttfDateTime
                    ? rosterConfigDetails.ttfDateTime
                    : null,
                  rosterConfigDetails.techEquipmentSolutionId !== 14 &&
                  rosterConfigDetails.tciDateTime
                    ? rosterConfigDetails.tciDateTime
                    : null,
                  rosterConfigDetails.techEquipmentSolutionId !== 14 &&
                  rosterConfigDetails.techTestDateTime
                    ? rosterConfigDetails.techTestDateTime
                    : null,
                  rosterConfigDetails.productionStartDate || null,
                  rosterConfigDetails.productTrainingDateTime || null,
                  rosterConfigDetails.trainerOracleId || null,
                  rosterConfigDetails.trainerName || "",
                  rosterConfigDetails.trainerEmail || "",
                  rosterConfigDetails.techEquipmentSolutionId === 14,
                ]}
                onClose={() => setShowCreateIdmTicket(false)}
              ></CreateIdmTickets>
            )}
          </Grid>
        </Grid>

        <Drawer
          anchor={"right"}
          open={showClassRequestDrawer["right"]}
          onClose={toggleClassRequestDrawer("right", false)}
          PaperProps={{
            sx: { width: "50%" },
          }}
        >
          <Grid container sx={{ height: "20%" }}>
            <Grid item xs={12}></Grid>
            <Grid item xs={4}>
              <Typography
                sx={{
                  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                  fontSize: 18,
                  fontWeight: "bold",
                  color: "#3299ff",
                  letterSpacing: "0.10em",
                  paddingLeft: 2,
                }}
              >
                Class request Details{" "}
              </Typography>
            </Grid>
            <Grid item xs={6.8}></Grid>
            <Grid item xs={0.2}>
              <CloseIcon
                fontSize="medium"
                onClick={toggleClassRequestDrawer("right", false)}
              ></CloseIcon>
            </Grid>
          </Grid>

          <ClassRequest
            onClose={toggleClassRequestDrawer("right", false)}
            classRequestDetails={classReqDetails}
          ></ClassRequest>
        </Drawer>
      </Paper>
    </>
  );
}
